import { ChangeEvent, ReactNode } from 'react';

import {
  ListItemSecondaryAction,
  MenuItem,
  TextField,
  Typography,
} from '@material-ui/core';

import {
  MetricsV1ColumnType,
  getTypeOrNull,
  useFactTableMeasuresQuery,
} from '@spotify/confidence-graphql';

// HLL = HyperLogLog, a probabilistic data structure for cardinality estimation
export function isHLLMeasurement(
  columnType: MetricsV1ColumnType | null | undefined,
): boolean {
  return columnType === MetricsV1ColumnType.ColumnTypeHllSketch;
}

type FactTableMeasurementSelectProps = {
  value: string;
  factTable?: string;
  label?: string;
  onChange: (
    measurement: string,
    columnType?: MetricsV1ColumnType | null,
  ) => void;
  disabled?: boolean;
  disabledOptions?: string[];
  disabledColumnTypes?: MetricsV1ColumnType[];
  id?: string;
  helperText?: ReactNode;
  name?: string;
  required?: boolean;
  emptyLabel?: string;
};

export function FactTableMeasurementSelect({
  label = 'Measurement',
  value,
  factTable,
  onChange,
  disabled,
  disabledOptions = [],
  disabledColumnTypes = [],
  helperText,
  id,
  name,
  required,
  emptyLabel = 'The number of fact rows',
}: FactTableMeasurementSelectProps) {
  const { data } = useFactTableMeasuresQuery({
    skip: !factTable,
    variables: { name: factTable || '' },
  });
  const measures = getTypeOrNull(data?.factTable)?.measures || [];

  const isDisabledByColumnType = (m: (typeof measures)[number]): boolean =>
    disabledColumnTypes.length > 0 &&
    Boolean(
      (m.column.type && disabledColumnTypes.includes(m.column.type)) ||
        (m.declaredType && disabledColumnTypes.includes(m.declaredType)),
    );

  const handleChange = (
    e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    const measurementName = e.target.value;
    const selectedMeasure = measures.find(
      m => m.column.name === measurementName,
    );
    onChange(measurementName, selectedMeasure?.column.type);
  };

  return (
    <TextField
      required={required}
      label={label}
      name={name ?? 'measurement'}
      margin="dense"
      fullWidth
      variant="outlined"
      onChange={handleChange}
      value={value}
      disabled={disabled}
      select
      id={id}
      helperText={helperText}
      SelectProps={{ displayEmpty: !!emptyLabel }}
      InputLabelProps={{
        shrink: true,
      }}
    >
      <MenuItem value="" divider disabled={!emptyLabel}>
        <em>{emptyLabel}</em>
      </MenuItem>
      {measures.map(option => {
        const disabledByType = isDisabledByColumnType(option);
        return (
          <MenuItem
            value={option.column.name}
            key={option.column.name}
            disabled={
              disabledOptions.includes(option.column.name) || disabledByType
            }
          >
            {option.column.name}
            {disabledByType && (
              <ListItemSecondaryAction>
                <Typography color="textSecondary" variant="body2">
                  Incompatible
                </Typography>
              </ListItemSecondaryAction>
            )}
          </MenuItem>
        );
      })}
    </TextField>
  );
}
